import React from 'react';

import { DataFrame, FieldConfig, LoadingState } from '@grafana/data';
import { t } from '@grafana/i18n';
import {
  PanelBuilders,
  QueryRunnerState,
  SceneComponentProps,
  SceneCSSGridItem,
  SceneCSSGridLayout,
  SceneDataProvider,
  SceneDataTransformer,
  sceneGraph,
  SceneObjectBase,
  SceneObjectState,
  SceneQueryRunner,
  VizPanelBuilder,
} from '@grafana/scenes';
import { Options as TextOptions } from '@grafana/schema/dist/esm/raw/composable/text/panelcfg/x/TextPanelCfg_types.gen';
import {
  FieldConfig as TimeSeriesFieldConfig,
  Options as TimeSeriesOptions,
} from '@grafana/schema/dist/esm/raw/composable/timeseries/panelcfg/x/TimeSeriesPanelCfg_types.gen';
import { DrawStyle, LoadingPlaceholder, StackingMode, useStyles2 } from '@grafana/ui';

import { reportAppInteraction, USER_EVENTS_ACTIONS, USER_EVENTS_PAGES } from '../../../services/analytics';
import { ValueSlugs } from '../../../services/enums';
import {
  buildFieldsQueryString,
  extractParserFromArray,
  getDetectedFieldType,
  isAvgField,
} from '../../../services/fields';
import { logger } from '../../../services/logger';
import { getQueryRunner, setLevelColorOverrides, setPanelNotices } from '../../../services/panel';
import { buildDataQuery, isQueryAvg } from '../../../services/query';
import { getQueryExpression } from '../../../services/queryRunner';
import { getFieldsPanelTypes, getPanelOption, getShowErrorPanels, setShowErrorPanels } from '../../../services/store';
import {
  getFieldGroupByVariable,
  getFieldsVariable,
  getJSONFieldsVariable,
  getValueFromFieldsFilter,
} from '../../../services/variableGetters';
import { ALL_VARIABLE_VALUE, DetectedFieldType, ParserType } from '../../../services/variables';
import { getPanelWrapperStyles, PanelMenu, TimeSeriesPanelType, TimeSeriesQueryType } from '../../Panels/PanelMenu';
import {
  getDetectedFieldsFrame,
  getDetectedFieldsFrameFromQueryRunnerState,
  getDetectedFieldsNamesFromQueryRunnerState,
  getDetectedFieldsParsersFromQueryRunnerState,
  ServiceScene,
} from '../ServiceScene';
import { FIELDS_BREAKDOWN_GRID_TEMPLATE_COLUMNS, FieldsBreakdownScene } from './FieldsBreakdownScene';
import { FieldsVizPanelWrapper } from './FieldsVizPanelWrapper';
import { LayoutSwitcher } from './LayoutSwitcher';
import { SelectLabelActionScene } from './SelectLabelActionScene';
import { ShowErrorPanelToggle } from './ShowErrorPanelToggle';
import { ShowFieldDisplayToggle } from './ShowFieldDisplayToggle';
import { MAX_NUMBER_OF_TIME_SERIES } from './TimeSeriesLimit';

export type FieldsPanelsType = 'text' | 'timeseries';

export interface FieldsAggregatedBreakdownSceneState extends SceneObjectState {
  body?: LayoutSwitcher;
  fieldsPanelsType: FieldsPanelsType;
  showErrorPanels: boolean;
  showErrorPanelToggle: boolean;
}

export class FieldsAggregatedBreakdownScene extends SceneObjectBase<FieldsAggregatedBreakdownSceneState> {
  constructor(state: Partial<FieldsAggregatedBreakdownSceneState>) {
    super({
      fieldsPanelsType: getFieldsPanelTypes() ?? 'timeseries',
      showErrorPanels: getShowErrorPanels(),
      showErrorPanelToggle: false,
      ...state,
    });

    this.addActivationHandler(this.onActivate.bind(this));
  }

  private onDetectedFieldsChange = (newState: QueryRunnerState) => {
    if (newState.data?.state === LoadingState.Done) {
      this.updateChildren(newState);
    }
  };

  private updateChildren(newState: QueryRunnerState, newParser: ParserType | undefined = undefined) {
    const detectedFieldsFrame = getDetectedFieldsFrameFromQueryRunnerState(newState);
    const newNamesField = getDetectedFieldsNamesFromQueryRunnerState(newState);
    const newParsersField = getDetectedFieldsParsersFromQueryRunnerState(newState);
    const cardinalityMap = this.calculateCardinalityMap(newState);

    // Iterate through all the layouts
    this.state.body?.state.layouts.forEach((layout) => {
      if (layout instanceof SceneCSSGridLayout) {
        // populate set of new list of fields
        const newFieldsSet = new Set<string>(newNamesField?.values);
        const updatedChildren = layout.state.children as SceneCSSGridItem[];

        // Iterate through all the existing panels
        for (let i = 0; i < updatedChildren.length; i++) {
          const gridItem = layout.state.children[i];
          if (gridItem instanceof SceneCSSGridItem) {
            const panelWrap = gridItem.state.body;
            if (panelWrap instanceof FieldsVizPanelWrapper) {
              const panel = panelWrap.state.viz;
              if (newParser) {
                const index = newNamesField?.values.indexOf(panel.state.title);
                const existingParser = index !== undefined && index !== -1 ? newParsersField?.values[index] : undefined;

                // If a new field filter was added that updated the parsers, we'll need to rebuild the query
                if (this.state.fieldsPanelsType === 'timeseries' && existingParser !== newParser) {
                  const dataTransformer = this.getTimeSeriesQueryRunnerForPanel(
                    panel.state.title,
                    detectedFieldsFrame,
                    panelWrap.state.queryType
                  );
                  panel.setState({
                    $data: dataTransformer,
                  });
                }
              }
              if (newFieldsSet.has(panel.state.title)) {
                // If the new response has this field, delete it from the set, but leave it in the layout
                newFieldsSet.delete(panel.state.title);
              } else {
                // Otherwise if the panel doesn't exist in the response, delete it from the layout
                updatedChildren.splice(i, 1);
                // And make sure to update the index, or we'll skip the next one
                i--;
              }
            } else {
              logger.warn('panel wrap is not FieldsVizPanelWrapper');
            }
          } else {
            logger.warn('gridItem is not SceneCSSGridItem');
          }
        }

        const fieldsToAdd = Array.from(newFieldsSet);
        const options = fieldsToAdd.map((fieldName) => fieldName);

        updatedChildren.push(...this.buildChildren(options));
        updatedChildren.sort(this.sortChildren(cardinalityMap));

        updatedChildren.map((child) => {
          this.subscribeToPanel(child);
        });

        layout.setState({
          children: updatedChildren,
        });
      } else {
        logger.warn('Layout is not SceneCSSGridLayout');
      }
    });
  }

  private sortChildren(cardinalityMap: Map<string, number>) {
    return (a: SceneCSSGridItem, b: SceneCSSGridItem) => {
      const aPanel = a.state.body as FieldsVizPanelWrapper;
      const bPanel = b.state.body as FieldsVizPanelWrapper;
      const aCardinality = cardinalityMap.get(aPanel.state.viz.state.title) ?? 0;
      const bCardinality = cardinalityMap.get(bPanel.state.viz.state.title) ?? 0;
      return bCardinality - aCardinality;
    };
  }

  private calculateCardinalityMap(newState?: QueryRunnerState) {
    const detectedFieldsFrame = getDetectedFieldsFrameFromQueryRunnerState(newState);
    const cardinalityMap = new Map<string, number>();
    if (detectedFieldsFrame?.length) {
      for (let i = 0; i < detectedFieldsFrame?.length; i++) {
        const name: string = detectedFieldsFrame.fields[0].values[i];
        const cardinality: number = detectedFieldsFrame.fields[1].values[i];
        cardinalityMap.set(name, cardinality);
      }
    }
    return cardinalityMap;
  }

  onActivate() {
    this.setState({
      body: this.build(),
    });

    const serviceScene = sceneGraph.getAncestor(this, ServiceScene);
    if (serviceScene.state.fieldsCount === undefined) {
      this.updateFieldCount();
    }

    this._subs.add(serviceScene.state.$detectedFieldsData?.subscribeToState(this.onDetectedFieldsChange));
    this._subs.add(this.subscribeToFieldsVar());
    this._subs.add(
      this.subscribeToState((newState, prevState) => {
        if (newState.fieldsPanelsType !== prevState.fieldsPanelsType) {
          // All query runners need to be rebuilt
          this.setState({
            body: this.build(),
          });
        }
      })
    );
  }

  private subscribeToFieldsVar() {
    const fieldsVar = getFieldsVariable(this);

    return fieldsVar.subscribeToState((newState, prevState) => {
      const serviceScene = sceneGraph.getAncestor(this, ServiceScene);
      const newParsers = newState.filters.map((f) => getValueFromFieldsFilter(f).parser);
      const oldParsers = prevState.filters.map((f) => getValueFromFieldsFilter(f).parser);

      const newParser = extractParserFromArray(newParsers);
      const oldParser = extractParserFromArray(oldParsers);

      if (newParser !== oldParser) {
        const detectedFieldsState = serviceScene.state.$detectedFieldsData?.state;
        if (detectedFieldsState) {
          this.updateChildren(detectedFieldsState, newParser);
        }
      }
    });
  }

  public build() {
    const groupByVariable = getFieldGroupByVariable(this);
    const options = groupByVariable.state.options.map((opt) => String(opt.value));

    const fieldsBreakdownScene = sceneGraph.getAncestor(this, FieldsBreakdownScene);
    fieldsBreakdownScene.state.search.reset();

    const children = this.buildChildren(options);

    const serviceScene = sceneGraph.getAncestor(this, ServiceScene);
    const cardinalityMap = this.calculateCardinalityMap(serviceScene.state.$detectedFieldsData?.state);
    children.sort(this.sortChildren(cardinalityMap));
    const childrenClones = children.map((child) => child.clone());

    // We must subscribe to the data providers for all children after the clone, or we'll see bugs in the row layout
    [...children, ...childrenClones].map((child) => {
      this.subscribeToPanel(child);
    });

    return new LayoutSwitcher({
      active: 'grid',
      layouts: [
        new SceneCSSGridLayout({
          autoRows: this.state.fieldsPanelsType === 'timeseries' ? '200px' : '35px',
          children: children,
          isLazy: true,
          templateColumns: FIELDS_BREAKDOWN_GRID_TEMPLATE_COLUMNS,
        }),
        new SceneCSSGridLayout({
          autoRows: this.state.fieldsPanelsType === 'timeseries' ? '200px' : '35px',
          children: childrenClones,
          isLazy: true,
          templateColumns: '1fr',
        }),
      ],
      options: [
        { label: t('components.service-scene.breakdowns.fields-aggregated-breakdown-scene.label.grid', 'Grid'), value: 'grid' },
        { label: t('components.service-scene.breakdowns.fields-aggregated-breakdown-scene.label.rows', 'Rows'), value: 'rows' },
      ],
    });
  }

  private subscribeToPanel(child: SceneCSSGridItem) {
    const panelWrap = child.state.body as FieldsVizPanelWrapper | undefined;
    if (panelWrap instanceof FieldsVizPanelWrapper) {
      const panel = panelWrap?.state.viz;
      if (panel) {
        this._subs.add(
          panel?.state.$data?.getResultsStream().subscribe((result) => {
            if (result.data.errors && result.data.errors.length > 0) {
              if (!this.state.showErrorPanels) {
                child.setState({ isHidden: true });
              } else {
                child.setState({ isHidden: false });
              }

              if (!this.state.showErrorPanelToggle) {
                this.setState({ showErrorPanelToggle: true });
              }
              this.updateFieldCount();
            } else {
              setPanelNotices(result, panel);
            }
          })
        );
      }
    } else {
      logger.warn('panel wrap is not FieldsVizPanelWrapper');
    }
  }

  public rebuildChangedPanels(changed: 'panelType' | 'queryType') {
    const detectedFieldsFrame = getDetectedFieldsFrame(this);
    const activeLayout = this.getActiveGridLayouts();
    const children: SceneCSSGridItem[] = [];
    const panelTypeFromLocalStorage =
      getPanelOption('panelType', [TimeSeriesPanelType.histogram, TimeSeriesPanelType.timeseries]) ??
      TimeSeriesPanelType.timeseries;

    activeLayout?.state.children.forEach((child) => {
      if (
        (child instanceof SceneCSSGridItem && this.state.showErrorPanels) ||
        (child instanceof SceneCSSGridItem && !child.state.isHidden)
      ) {
        const panelWrappers = sceneGraph.findDescendents(child, FieldsVizPanelWrapper);
        if (panelWrappers.length) {
          if (changed === 'panelType') {
            children.push(
              this.rebuildPanelOnPanelTypeChange(panelWrappers, panelTypeFromLocalStorage, detectedFieldsFrame, child)
            );
          } else {
            children.push(
              this.rebuildPanelOnQueryTypeChange(panelWrappers, detectedFieldsFrame, panelTypeFromLocalStorage, child)
            );
          }
        }
      }
    });

    if (children.length) {
      activeLayout?.setState({
        children,
      });
    }
  }

  private rebuildPanelOnPanelTypeChange = (
    panelWrappers: FieldsVizPanelWrapper[],
    panelTypeFromLocalStorage: TimeSeriesPanelType,
    detectedFieldsFrame: DataFrame | undefined,
    child: SceneCSSGridItem
  ) => {
    // Will only be one panel as a child of CSSGridItem
    const panelWrap = panelWrappers[0];
    const panel = panelWrap.state.viz;
    const labelName = panel.state.title;

    const panelTypeChanged =
      panelWrap.state.supportsHistogram &&
      panelTypeFromLocalStorage !== panel.state.pluginId &&
      // Don't rebuild count_over_time queries
      panelWrap.state.queryType === TimeSeriesQueryType.avg;

    if (panelTypeChanged) {
      const newChild = this.buildChild(
        labelName,
        detectedFieldsFrame,
        panelTypeFromLocalStorage,
        panelWrap.state.queryType
      );
      if (newChild) {
        return newChild;
      }
    }

    return child;
  };

  private rebuildPanelOnQueryTypeChange = (
    panelWrappers: FieldsVizPanelWrapper[],
    detectedFieldsFrame: DataFrame | undefined,
    panelTypeFromLocalStorage: TimeSeriesPanelType,
    child: SceneCSSGridItem
  ) => {
    // Will only be one panel as a child of CSSGridItem
    const panelWrap = panelWrappers[0];
    const panel = panelWrap.state.viz;
    const labelName = panel.state.title;
    const panelExpr = getQueryExpression(panel);
    const expressionQueryType = isQueryAvg(panelExpr) ? TimeSeriesQueryType.avg : TimeSeriesQueryType.count;

    const queryTypeChanged = panelWrap.state.queryType !== expressionQueryType;

    if (queryTypeChanged) {
      const newChild = this.buildChild(
        labelName,
        detectedFieldsFrame,
        panel.state.pluginId === 'timeseries' ? panelTypeFromLocalStorage : TimeSeriesPanelType.timeseries,
        panelWrap.state.queryType
      );

      if (newChild) {
        return newChild;
      }
    }

    return child;
  };

  private buildChildren(options: string[]): SceneCSSGridItem[] {
    const children: SceneCSSGridItem[] = [];
    const detectedFieldsFrame = getDetectedFieldsFrame(this);
    const panelType =
      getPanelOption('panelType', [TimeSeriesPanelType.timeseries, TimeSeriesPanelType.histogram]) ??
      TimeSeriesPanelType.timeseries;
    for (const option of options) {
      if (option === ALL_VARIABLE_VALUE || !option) {
        continue;
      }

      const fieldType = getDetectedFieldType(option, detectedFieldsFrame);
      const child = this.buildChild(
        option,
        detectedFieldsFrame,
        panelType,
        isAvgField(fieldType) ? TimeSeriesQueryType.avg : TimeSeriesQueryType.count
      );
      if (child) {
        children.push(child);
      }
    }
    return children;
  }

  private buildChild(
    labelName: string,
    detectedFieldsFrame: DataFrame | undefined,
    panelType: TimeSeriesPanelType,
    queryType: TimeSeriesQueryType
  ) {
    if (labelName === ALL_VARIABLE_VALUE || !labelName) {
      return;
    }

    const fieldType = getDetectedFieldType(labelName, detectedFieldsFrame);

    let body: VizPanelBuilder<TextOptions, FieldConfig> | VizPanelBuilder<TimeSeriesOptions, TimeSeriesFieldConfig>;
    if (this.state.fieldsPanelsType === 'text') {
      const dataTransformer = this.getEstimatedCardinalityQueryRunnerForPanel(labelName);
      body = this.buildText(labelName, fieldType, dataTransformer);
    } else {
      const dataTransformer = this.getTimeSeriesQueryRunnerForPanel(labelName, detectedFieldsFrame, queryType);
      body = this.buildTimeSeries(fieldType, labelName, dataTransformer, panelType, queryType);
    }

    body.setShowMenuAlways(true);

    const viz = body.build();

    return new SceneCSSGridItem({
      body: new FieldsVizPanelWrapper({
        viz: viz,
        queryType,
        supportsHistogram: isAvgField(fieldType) || fieldType === 'int',
      }),
    });
  }

  private buildText = (
    labelName: string,
    fieldType: DetectedFieldType | undefined,
    queryProvider: SceneDataProvider
  ): VizPanelBuilder<TextOptions, FieldConfig> => {
    const text = PanelBuilders.text()
      .setTitle(labelName)
      .setData(queryProvider)
      .setHeaderActions(
        new SelectLabelActionScene({
          fieldType: ValueSlugs.field,
          hasNumericFilters:
            fieldType === 'int' || fieldType === 'float' || fieldType === 'bytes' || fieldType === 'duration',
          labelName: String(labelName),
        })
      );

    text.setOption('content', '');
    return text;
  };

  private buildTimeSeries = (
    fieldType: 'boolean' | 'bytes' | 'duration' | 'float' | 'int' | 'string' | undefined,
    labelName: string,
    dataTransformer: SceneDataTransformer | SceneQueryRunner,
    panelType: TimeSeriesPanelType,
    queryType: TimeSeriesQueryType
  ): VizPanelBuilder<TimeSeriesOptions, TimeSeriesFieldConfig> => {
    let body;
    let headerActions = [];
    if (queryType === TimeSeriesQueryType.count) {
      body = PanelBuilders.timeseries()
        .setOption('annotations', { multiLane: true })
        .setTitle(labelName)
        .setData(dataTransformer)
        .setMenu(
          new PanelMenu({
            fieldType,
            panelType: fieldType === 'int' ? panelType : undefined,
          })
        )
        .setCustomFieldConfig('stacking', { mode: StackingMode.Normal })
        .setCustomFieldConfig('fillOpacity', 100)
        .setCustomFieldConfig('lineWidth', 0)
        .setCustomFieldConfig('pointSize', 0)
        .setCustomFieldConfig('drawStyle', DrawStyle.Bars)
        .setOverrides(setLevelColorOverrides);

      headerActions.push(
        new SelectLabelActionScene({
          fieldType: ValueSlugs.field,
          hasNumericFilters: fieldType === 'int',
          labelName: String(labelName),
        })
      );
    } else {
      if (panelType === TimeSeriesPanelType.histogram) {
        body = PanelBuilders.histogram();
      } else {
        body = PanelBuilders.timeseries().setOption('annotations', { multiLane: true });
      }
      body
        .setTitle(labelName)
        .setData(dataTransformer)
        .setUnit('short')
        .setMenu(new PanelMenu({ panelType, fieldType }));
      headerActions.push(
        new SelectLabelActionScene({
          fieldType: ValueSlugs.field,
          hideValueDrilldown: true,
          labelName: String(labelName),
        })
      );
    }
    body.setSeriesLimit(MAX_NUMBER_OF_TIME_SERIES);
    body.setHeaderActions(headerActions);
    return body;
  };

  private getTimeSeriesQueryRunnerForPanel(
    optionValue: string,
    detectedFieldsFrame: DataFrame | undefined,
    queryType: TimeSeriesQueryType
  ) {
    const fieldsVariable = getFieldsVariable(this);
    const jsonVariable = getJSONFieldsVariable(this);
    // pass in current panel state
    const queryString = buildFieldsQueryString(
      optionValue,
      fieldsVariable,
      detectedFieldsFrame,
      jsonVariable,
      queryType
    );
    const query = buildDataQuery(queryString, {
      legendFormat: queryType === TimeSeriesQueryType.avg ? optionValue : `{{${optionValue}}}`,
      refId: optionValue,
    });

    return getQueryRunner([query]);
  }

  private getEstimatedCardinalityQueryRunnerForPanel(optionValue: string) {
    return new SceneDataTransformer({
      transformations: [],
    });
  }

  private getActiveGridLayouts() {
    return (this.state.body?.state.layouts.find((l) => l.isActive) ?? this.state.body?.state.layouts[0]) as
      | SceneCSSGridLayout
      | undefined;
  }

  private updateFieldCount() {
    const activeLayout = this.getActiveGridLayouts();
    const activeLayoutChildren = activeLayout?.state.children as SceneCSSGridItem[] | undefined;
    const activePanels = activeLayoutChildren?.filter((child) => this.state.showErrorPanels || !child.state.isHidden);

    const fieldsBreakdownScene = sceneGraph.getAncestor(this, FieldsBreakdownScene);
    fieldsBreakdownScene.state.changeFieldCount?.(activePanels?.length ?? 0);
  }

  public toggleErrorPanels(event: React.ChangeEvent<HTMLInputElement>) {
    const showErrorPanels = event.target.checked;
    this.setState({ showErrorPanels });
    setShowErrorPanels(showErrorPanels);
    const serviceScene = sceneGraph.getAncestor(this, ServiceScene);
    reportAppInteraction(USER_EVENTS_PAGES.service_details, USER_EVENTS_ACTIONS.service_details.toggle_error_panels, {
      checked: showErrorPanels,
    });
    // No need to re-run queries if we have the query runners in the panel with the error state.
    if (!showErrorPanels) {
      if (serviceScene.state.$detectedFieldsData?.state) {
        this.updateChildren(serviceScene.state.$detectedFieldsData?.state);
      } else {
        this.setState({
          body: this.build(),
        });
      }
      // But otherwise we need to re-run any query for panels we don't have query runners for.
      // @todo We could make this more efficient and only run queries on panels that are in the latest detected_fields response that don't have an associated panel
    } else {
      this.setState({
        body: this.build(),
      });
    }
  }

  public static ShowErrorPanelToggle = ShowErrorPanelToggle;

  public static ShowFieldDisplayToggle = ShowFieldDisplayToggle;

  public static Selector({ model }: SceneComponentProps<FieldsAggregatedBreakdownScene>) {
    const { body } = model.useState();
    return <>{body && <LayoutSwitcher.Selector model={body} />}</>;
  }

  public static Component = ({ model }: SceneComponentProps<FieldsAggregatedBreakdownScene>) => {
    const { body } = model.useState();
    const styles = useStyles2(getPanelWrapperStyles);
    if (body) {
      return <div className={styles.panelWrapper}>{body && <body.Component model={body} />}</div>;
    }

    return <LoadingPlaceholder text={t('components.service-scene.breakdowns.fields-aggregated-breakdown-scene.text-loading', 'Loading...')} />;
  };
}
